import React from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { useIntl } from '../../util/reactIntl';
import { toggleFavoriteListing, isListingFavorited } from '../../ducks/favorites.duck';

import css from './FavoriteButton.module.css';

const HeartIcon = props => {
  const { filled } = props;
  return filled ? (
    <svg className={css.icon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 21s-6.7-4.2-9.6-8.2C.6 10.1 1 6.6 3.6 4.9c2.3-1.5 5.2-1 6.9 1.1l1.5 1.8 1.5-1.8c1.7-2.1 4.6-2.6 6.9-1.1 2.6 1.7 3 5.2 1.2 7.9C18.7 16.8 12 21 12 21z" />
    </svg>
  ) : (
    <svg
      className={css.icon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 21s-6.7-4.2-9.6-8.2C.6 10.1 1 6.6 3.6 4.9c2.3-1.5 5.2-1 6.9 1.1l1.5 1.8 1.5-1.8c1.7-2.1 4.6-2.6 6.9-1.1 2.6 1.7 3 5.2 1.2 7.9C18.7 16.8 12 21 12 21z" />
    </svg>
  );
};

/**
 * A heart-shaped toggle button that adds/removes a listing from the current
 * user's favorites (stored in their privateData - see ducks/favorites.duck.js).
 * Safe to nest inside a NamedLink (e.g. ListingCard) - stops the click from
 * triggering the parent link's navigation.
 *
 * @component
 * @param {Object} props
 * @param {string} props.listingId - the listing's plain UUID string
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 */
const FavoriteButton = props => {
  const { listingId, className, rootClassName } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();

  const currentUser = useSelector(state => state.user.currentUser);
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const togglingListingId = useSelector(state => state.favorites.togglingListingId);

  const isFavorited = isListingFavorited(currentUser, listingId);
  const isToggling = togglingListingId === listingId;

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };
      history.push(createResourceLocatorString('LoginPage', routeConfiguration, {}, {}), state);
      return;
    }

    if (!isToggling) {
      dispatch(toggleFavoriteListing(listingId));
    }
  };

  const classes = classNames(rootClassName || css.root, className, {
    [css.favorited]: isFavorited,
  });

  const labelId = isFavorited ? 'FavoriteButton.removeLabel' : 'FavoriteButton.addLabel';

  return (
    <button
      type="button"
      className={classes}
      onClick={handleClick}
      disabled={isToggling}
      aria-pressed={isFavorited}
      aria-label={intl.formatMessage({ id: labelId })}
    >
      <HeartIcon filled={isFavorited} />
    </button>
  );
};

export default FavoriteButton;
